// Setup: Autostart, Engine, Datenordner, Lizenzen. Die Wallpaper-Einstellungen stehen auf der Wallpaper-Seite.
import { h, type VNode } from 'snabbdom';
import { api, type Setup } from '../api';
import { appPieces, isPublic, setAppPieces, type AppPieces } from '../util';
import { t } from '../i18n';
import { deviceBox } from './setupdevice';
import { visitsBox } from './setupvisits';
import { SIZE, setSize, uiSize } from '../zoom';

export class SetupCtrl {
  data: Setup | null = null;
  error = '';

  constructor(readonly redraw: () => void) {}

  async load() {
    if (isPublic) return this.redraw();
    try {
      this.data = await api.setup();
      this.error = '';
    } catch (e) {
      this.error = t('Setup nicht geladen: {e}', { e: (e as Error).message });
    }
    this.redraw();
  }

  async toggle(target: 'server' | 'wallpaper', on: boolean) {
    try {
      const st = await api.setAutostart(target, on);
      if (this.data) this.data.autostart[target] = st;
      this.error = '';
    } catch (e) {
      this.error = (e as Error).message;
    }
    this.redraw();
  }
}

/** Hinweise des Servers (Autostart.java) übersetzen */
function reason(r: string | null | undefined): string {
  if (!r) return '';
  const m = /^Renderer nicht gefunden: (.*)$/.exec(r);
  return m ? t('Renderer nicht gefunden: {p}', { p: m[1] }) : t(r);
}

function autostartRow(c: SetupCtrl, target: 'server' | 'wallpaper', label: string, hint: string): VNode {
  const st = c.data!.autostart[target];
  return h('div.fld', [
    h('label.chk', [
      h('input', {
        attrs: { type: 'checkbox', disabled: !st.available && !st.enabled },
        props: { checked: st.enabled },
        on: { change: (e: Event) => c.toggle(target, (e.target as HTMLInputElement).checked) },
      }),
      ' ' + label,
    ]),
    h('p.muted.small', st.available ? hint : reason(st.reason)),
  ]);
}

export function setupView(c: SetupCtrl): VNode {
  const d = c.data;
  if (!d && !isPublic) return c.error ? h('p.error', c.error) : h('p.muted', t('lade …'));
  const pieceOpt = (p: AppPieces, label: string, hint: string) =>
    h('label.set-choice', { class: { active: appPieces() === p } }, [
      h('input', { attrs: { type: 'radio', name: 'pieces' }, props: { checked: appPieces() === p }, on: { change: () => setAppPieces(p, c.redraw) } }),
      h(`div.set-preview.cg-wrap.set-${p}`, ['king', 'queen', 'bishop', 'knight', 'rook', 'pawn'].map(r => h(`piece.${r}.white`))),
      h('div', [h('b', label), h('p.muted.small', hint)]),
    ]);
  return h('section.setup-page', [
    h('div.setup', [
      h('div.hd', h('h3', t('Figuren'))),
      h('div.fld', [
        pieceOpt('genelec', 'Genelec', t('König 8380A, Dame 8361A, Läufer 8351B, Springer 1031A, Turm 7370A, Bauer 8020D.')),
        pieceOpt('cburnett', 'lichess cburnett', t('Der klassische Satz von lichess.')),
        h('p.muted.small', isPublic ? t('Gilt in diesem Browser für alle Bretter.') : t('Gilt für alle Bretter: Analyse, Spielen, Editor, Foto und Wallpaper.')),
      ]),
    ]),
    h('div.setup', [
      h('div.hd', h('h3', t('Größe'))),
      h('div.fld', [
        h('div.row', [h('label', { attrs: { for: 'ui-size' } }, t('Eigene Größe')), h('output', `${uiSize()} %`)]),
        h('input#ui-size', {
          attrs: { type: 'range', min: String(SIZE.min), max: String(SIZE.max), step: String(SIZE.step) },
          props: { value: String(uiSize()) },
          on: { input: (e: Event) => { setSize(+(e.target as HTMLInputElement).value); c.redraw(); } },
        }),
        h('p.muted.small', t('Die Oberfläche wächst mit dem Fenster mit; die eigene Größe kommt dazu. Gilt in diesem Browser, am Handy bleibt sie fest.')),
      ]),
    ]),
    ...(!d ? [] : [h('div.setup', [
      h('div.hd', h('h3', t('Beim Anmelden starten'))),
      autostartRow(c, 'server', 'Schachserver', t('Startet den Server ohne Browserfenster; erreichbar über das Symbol in der Menüleiste.')),
      autostartRow(c, 'wallpaper', t('Wallpaper'), t('Startet nur das Wallpaper mit der aktuellen Playlist. Braucht den Server nicht.')),
    ]),
    h('div.setup', [
      h('div.hd', h('h3', t('Engine'))),
      h('div.fld', [
        h('div.row', [h('span', 'Stockfish'), h('span.muted', d.engine.available ? d.engine.name || t('gefunden') : t('nicht gefunden'))]),
        h('code.path', d.engine.path),
        h('p.muted.small', t('Stockfish startet nur, solange analysiert wird, und beendet sich nach zwei Minuten Leerlauf.')),
      ]),
      h('div.fld', [h('div.row', [h('span', t('Wallpaper-Renderer'))]), h('code.path', d.renderer)]),
    ]),
    h('div.setup', [
      h('div.hd', h('h3', t('Daten'))),
      h('div.fld', [
        h('code.path', d.dataDir),
        h('p.muted.small', t('Bibliothek (schachserver.db), Wallpaper-Playlist und Protokoll des Renderers. Alles bleibt auf diesem Rechner.')),
      ]),
    ])]),
    deviceBox(c.redraw),
    visitsBox(c.redraw),
    h('div.setup', [
      h('div.hd', h('h3', t('Lizenzen'))),
      h('div.fld', [
        h('p.small', t('Schachserver steht unter GPL-3.0. Es nutzt chessground, chessops und scalachess von lichess.org, Stockfish, Maia (CSSLab, Universität Toronto), Rodent IV und Figurensätze aus lila. Eröffnungsnamen und Rätsel stammen aus den lichess-Datenbanken (gemeinfrei, CC0).')),
        h('a.link', { attrs: { href: '/recognition/NOTICE.md', target: '_blank' } }, t('Figurensätze und Urheber')),
      ]),
    ]),
    c.error ? h('p.error', c.error) : null,
  ]);
}
